import { useEffect, useState } from 'react';
import { getReportRecipients, saveReportRecipients, emailReport } from '../api/analysis';
import { useAuth } from '../context/AuthContext';

const EMAIL_RE = /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/;

function EmailListField({ label, list, setList, placeholder }) {
  const [draft, setDraft] = useState('');
  const [err, setErr] = useState('');

  function add() {
    const v = draft.trim().toLowerCase();
    if (!v) return;
    if (!EMAIL_RE.test(v)) return setErr(`"${draft}" is not a valid email address`);
    setErr('');
    if (!list.includes(v)) setList([...list, v]);
    setDraft('');
  }

  return (
    <div className="mb-4">
      <div className="text-xs font-medium text-gray-600 mb-1">{label}</div>
      {err && <div className="mb-1 text-xs text-red-700">{err}</div>}
      <ul className="mb-2 space-y-1">
        {list.length === 0 && <li className="text-sm text-gray-500">None.</li>}
        {list.map((e) => (
          <li key={e} className="flex items-center justify-between text-sm bg-gray-50 border border-gray-200 rounded px-3 py-1.5">
            <span>{e}</span>
            <button type="button" onClick={() => setList(list.filter((x) => x !== e))} className="text-xs text-red-600 hover:underline">
              Remove
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder={placeholder}
          className="flex-1 border border-gray-300 rounded px-3 py-1.5 text-sm"
        />
        <button type="button" onClick={add} className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50">
          + Add
        </button>
      </div>
    </div>
  );
}

export default function SendGpReportModal({ month, onClose }) {
  const { isAdmin } = useAuth();
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  useEffect(() => {
    getReportRecipients()
      .then((r) => {
        setTo(r.recipients || []);
        setCc(r.cc || []);
      })
      .catch(() => setError('Could not load the saved recipients'))
      .finally(() => setLoading(false));
  }, []);

  async function act(fn, okMessage) {
    setBusy(true);
    setError('');
    setDone('');
    try {
      await fn();
      if (okMessage) setDone(okMessage);
    } catch (e) {
      setError(e.response?.data?.message || 'Request failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-5">
        <h2 className="text-base font-semibold text-gray-800 mb-1">Send GP Report</h2>
        <p className="text-xs text-gray-500 mb-4">
          The report for {month} is recalculated on the server when you press Send — it always matches the numbers on the Analysis page.
        </p>

        {error && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
        {done && <div className="mb-3 text-sm text-green-800 bg-green-50 border border-green-200 rounded px-3 py-2">{done}</div>}

        {loading ? (
          <p className="text-sm text-gray-500 mb-4">Loading…</p>
        ) : (
          <>
            <EmailListField label="Recipients (To)" list={to} setList={setTo} placeholder="name@company.com" />
            <EmailListField label="Cc (optional)" list={cc} setList={setCc} placeholder="name@company.com" />
          </>
        )}

        <div className="flex flex-wrap justify-between gap-2">
          {isAdmin ? (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                act(async () => {
                  const r = await saveReportRecipients(to, cc);
                  setTo(r.recipients);
                  setCc(r.cc);
                }, 'Recipient list saved.')
              }
              className="text-sm text-blue-600 hover:underline disabled:opacity-50"
            >
              Save as default list
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50">
              {done ? 'Close' : 'Cancel'}
            </button>
            <button
              type="button"
              disabled={busy || to.length === 0}
              onClick={() =>
                act(async () => {
                  const r = await emailReport(month, to, cc);
                  const ccPart = r.ccSent ? ` (cc ${r.ccSent})` : '';
                  setDone(`Sent to ${r.sent} recipient${r.sent > 1 ? 's' : ''}${ccPart}.`);
                })
              }
              className="px-3 py-1.5 text-sm rounded bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white"
            >
              {busy ? 'Working…' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
